import React, { useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Line } from "react-chartjs-2";
import { clearHistory } from "../../../store/slices/dartSlice";
import { clearStatistics } from "../../../store/slices/dartSlice";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const CurrentRoundHistory = () => {
  const dispatch = useDispatch();
  const chartRef = useRef(null);
  const { t } = useTranslation();

  // Pull round history from Redux
  const roundHistory = useSelector((state) => state.dartGame.roundHistory);

  // Reverse for chronological display
  const reversed = [...roundHistory].reverse();
  const scores = reversed.map((r) => r.totalScore);
  const labels = reversed.map((_, i) => `${t("history.round")} ${i + 1}`);

  const handleClear = () => {
    if (window.confirm(t("history.confirmClear"))) {
      dispatch(clearHistory());
      dispatch(clearStatistics());
    }
  };

  const data = {
    labels,
    datasets: [
      {
        label: t("history.chart.score"),
        data: scores,
        fill: false,
        tension: 0.3,
        borderWidth: 3,
        borderColor: (context) => {
          const { ctx, chartArea } = context.chart;
          if (!chartArea) return "#FFA500";
          const gradient = ctx.createLinearGradient(
            chartArea.left,
            0,
            chartArea.right,
            0
          );
          gradient.addColorStop(0, "#FFA500");
          gradient.addColorStop(1, "#FF4500");
          return gradient;
        },
        pointBackgroundColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#fff",
        bodyColor: "#fff",
        padding: 10,
        cornerRadius: 8,
        titleFont: {
          family: "'Inter', sans-serif",
          size: 14,
          weight: "bold",
        },
        bodyFont: {
          family: "'Inter', sans-serif",
          size: 13,
        },
        callbacks: {
          label: function (context) {
            return `${t("history.tooltip.score")}: ${context.raw}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
          drawBorder: false,
        },
        ticks: {
          color: "#aaa",
          font: {
            family: "'Inter', sans-serif",
            size: 12,
          },
        },
      },
      y: {
        ticks: {
          color: "#aaa",
          font: {
            family: "'Inter', sans-serif",
            size: 12,
          },
        },
      },
    },
    interaction: {
      intersect: false,
      mode: "index",
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-secondary/80 backdrop-blur-sm p-4 rounded-xl shadow-lg w-full max-w-4xl mx-auto border border-primary/20"
    >
      <h2 className="text-xl font-semibold mb-4 text-test">
        {t("history.currentRoundStatistics")}
      </h2>

      {/* Chart */}
      {roundHistory.length > 0 && (
        <div className="w-full h-64 sm:h-80 lg:h-96 mb-8 bg-primary/30 p-4 rounded-lg border border-primary/20">
          <Line ref={chartRef} data={data} options={options} />
        </div>
      )}

      {/* History List */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 overflow-y-auto max-h-100">
        {roundHistory.map((round, idx) => {
          const roundNumber = roundHistory.length - idx;
          return (
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2, delay: idx * 0.05 }}
              className="bg-primary/80 border border-primary/30 rounded-xl shadow p-4 text-white flex flex-col hover:shadow-md transition-all duration-200"
            >
              <div className="flex justify-between items-center mb-2">
                <span className="text-test font-medium">
                  {t("history.round")} {roundNumber}
                </span>
                <span className="text-sm text-gray-300 bg-secondary/50 px-2 py-1 rounded-full">
                  {round.totalScore} {t("history.points")}
                </span>
              </div>
              <div className="flex flex-wrap text-sm text-gray-200">
                {round.hits.map((hit, i) => (
                  <span key={i} className="mr-2 mb-1">
                    <span className="font-medium text-white">{hit.label}</span>
                    <span className="ml-1 text-gray-400">({hit.score})</span>
                    {i < round.hits.length - 1 && (
                      <span className="mx-1 text-gray-600">|</span>
                    )}
                  </span>
                ))}
              </div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default CurrentRoundHistory;
